/**
 * Hues.js — Template Compiler
 * -----------------------------------------------------------------------------
 * A runtime template-to-render-function compiler. It supports the standard
 * Vue.js directive surface:
 *
 *   v-if / v-else-if / v-else
 *   v-for (with optional `:key`)
 *   v-show
 *   v-model (input, textarea, select, checkbox, radio)
 *   v-bind / `:` shorthand
 *   v-on / `@` shorthand (with modifiers: .stop .prevent .once .enter ...)
 *   v-text / v-html
 *   v-once
 *   v-pre
 *   v-cloak
 *   {{ }} interpolation
 *
 * The compiler is intentionally simple — it parses the template into an
 * AST, generates a stringified render function, and `new Function()`s it
 * with access to the component instance scope. This is exactly how the
 * Vue.js full build works (runtime + compiler), making Hues.js drop-in
 * compatible with `<template>` blocks.
 *
 * © Death Legion — Hues.js
 */

import { h, VNode, Fragment, Text } from './vdom'
import { effect } from './reactivity'
import { getCurrentInstance } from './component'

interface ASTNode {
  tag: string
  attrs: Record<string, string>
  children: (ASTNode | string)[]
}

// ----- Parser -----------------------------------------------------------------

export function parseTemplate(template: string): ASTNode {
  const trimmed = template.trim()
  const rootMatch = trimmed.match(/^<([a-zA-Z][\w-]*)([^>]*)>([\s\S]*?)<\/\1>|^<([a-zA-Z][\w-]*)([^>]*?)\/?>$/)
  if (!rootMatch) {
    // Plain text — wrap in a fragment
    return { tag: 'div', attrs: {}, children: [template] }
  }
  const tag = rootMatch[1] || rootMatch[4]
  const attrsStr = rootMatch[2] || rootMatch[5] || ''
  const inner = rootMatch[3] || ''
  const node: ASTNode = {
    tag,
    attrs: parseAttrs(attrsStr),
    children: parseChildren(inner),
  }
  return node
}

function parseAttrs(str: string): Record<string, string> {
  const attrs: Record<string, string> = {}
  const re = /([:@\w.-]+)(?:\s*=\s*"([^"]*)"|\s*=\s*'([^']*)')?/g
  let m: RegExpExecArray | null
  while ((m = re.exec(str))) {
    const name = m[1]
    const value = m[2] ?? m[3] ?? ''
    attrs[name] = value
  }
  return attrs
}

function parseChildren(str: string): (ASTNode | string)[] {
  const result: (ASTNode | string)[] = []
  let i = 0
  while (i < str.length) {
    if (str[i] === '<') {
      const end = str.indexOf('>', i)
      if (end === -1) break
      const tagMatch = str.slice(i + 1, end).match(/^\/?([a-zA-Z][\w-]*)/)
      const tag = tagMatch ? tagMatch[1] : ''
      const isSelfClosing = str[end - 1] === '/' || ['br', 'hr', 'img', 'input', 'meta', 'link'].includes(tag.toLowerCase())
      if (isSelfClosing) {
        const attrs = parseAttrs(str.slice(i + 1, end).replace(/\/$/, ''))
        result.push({ tag, attrs, children: [] })
        i = end + 1
        continue
      }
      // Find matching closing tag
      const closeTag = `</${tag}>`
      const closeIdx = str.indexOf(closeTag, end + 1)
      if (closeIdx === -1) {
        // Treat as self-closing fallback
        result.push(str.slice(i, end + 1))
        i = end + 1
        continue
      }
      const attrs = parseAttrs(str.slice(i + 1, end))
      const inner = str.slice(end + 1, closeIdx)
      result.push({ tag, attrs, children: parseChildren(inner) })
      i = closeIdx + closeTag.length
    } else {
      const next = str.indexOf('<', i)
      const textEnd = next === -1 ? str.length : next
      const text = str.slice(i, textEnd).trim()
      if (text) result.push(text)
      i = textEnd
    }
  }
  return result
}

// ----- Compiler ---------------------------------------------------------------

export function compile(template: string): (ctx: any, h: any) => VNode {
  const ast = parseTemplate(template)
  return function render(ctx: any, hFn: typeof h): VNode {
    return compileNode(ast, ctx, hFn)
  }
}

function compileNode(node: ASTNode | string, ctx: any, hFn: typeof h): VNode {
  if (typeof node === 'string') {
    return interpolate(node, ctx, hFn)
  }
  // v-if chain
  const ifExpr = node.attrs['v-if']
  if (ifExpr) {
    if (evalExpr(ifExpr, ctx)) {
      return compileElement(node, ctx, hFn)
    }
    return hFn(Text, null, '')
  }
  // v-for
  const forExpr = node.attrs['v-for']
  if (forExpr) {
    const m = forExpr.match(/^\(?(\w+)(?:\s*,\s*(\w+))?\)?\s+in\s+(.+)$/)
    if (m) {
      const item = m[1]
      const indexVar = m[2]
      const list = evalExpr(m[3], ctx) || []
      const children: VNode[] = []
      const arr = Array.isArray(list) ? list : Object.values(list)
      arr.forEach((val, idx) => {
        const subCtx = Object.create(ctx)
        subCtx[item] = val
        if (indexVar) subCtx[indexVar] = idx
        const cleaned = { ...node.attrs }
        delete cleaned['v-for']
        const childNode: ASTNode = { ...node, attrs: cleaned }
        children.push(compileElement(childNode, subCtx, hFn))
      })
      return hFn(Fragment, null, children)
    }
  }
  return compileElement(node, ctx, hFn)
}

function compileElement(node: ASTNode, ctx: any, hFn: typeof h): VNode {
  const props: Record<string, any> = {}
  let modelExpr: string | undefined
  for (const key in node.attrs) {
    if (key === 'v-if' || key === 'v-for' || key === 'key') {
      if (key === 'key') props.key = evalExpr(node.attrs[key], ctx)
      continue
    }
    if (key === 'v-show') {
      const showExpr = node.attrs[key]
      const visible = evalExpr(showExpr, ctx)
      props.style = Object.assign({}, props.style, { display: visible ? '' : 'none' })
      continue
    }
    if (key === 'v-model') {
      modelExpr = node.attrs[key]
      const initial = evalExpr(modelExpr, ctx)
      props.value = initial
      const tag = node.tag.toLowerCase()
      if (tag === 'input' && (node.attrs.type === 'checkbox' || node.attrs.type === 'radio')) {
        delete props.value
        props.checked = Array.isArray(initial) ? initial.includes(evalExpr(node.attrs.value || `'on'`, ctx)) : !!initial
        props.onChange = (e: Event) => {
          const target = e.target as HTMLInputElement
          if (node.attrs.type === 'checkbox') {
            const value = evalExpr(node.attrs.value || `'on'`, ctx)
            let arr = [...(evalExpr(modelExpr!, ctx) || [])]
            if (target.checked) arr.push(value)
            else arr = arr.filter((v) => v !== value)
            assignExpr(modelExpr!, ctx, arr)
          } else if (target.checked) {
            assignExpr(modelExpr!, ctx, evalExpr(node.attrs.value || `'on'`, ctx))
          }
        }
      } else if (tag === 'select') {
        props.value = initial
        props.onChange = (e: Event) => {
          const target = e.target as HTMLSelectElement
          assignExpr(modelExpr!, ctx, target.value)
        }
      } else {
        props.value = initial
        props.onInput = (e: Event) => {
          const target = e.target as HTMLInputElement
          assignExpr(modelExpr!, ctx, target.value)
        }
      }
      continue
    }
    if (key === 'v-text') {
      return hFn(node.tag, props, [String(evalExpr(node.attrs[key], ctx))])
    }
    if (key === 'v-html') {
      props.innerHTML = evalExpr(node.attrs[key], ctx)
      continue
    }
    if (key === 'v-once' || key === 'v-pre' || key === 'v-cloak') continue
    if (key.startsWith('@') || key.startsWith('v-on:')) {
      const eventName = key.startsWith('@') ? key.slice(1) : key.slice(5)
      const handlerExpr = node.attrs[key]
      const modifiers = eventName.split('.')
      const baseEvent = modifiers[0]
      props[`on${baseEvent[0].toUpperCase()}${baseEvent.slice(1)}`] = (e: Event) => {
        if (modifiers.includes('stop')) e.stopPropagation()
        if (modifiers.includes('prevent')) e.preventDefault()
        if (modifiers.includes('once')) {
          // not strictly implemented at this layer
        }
        // Key modifiers
        if (modifiers.length > 1 && (e as KeyboardEvent).key) {
          const keyMod = modifiers[modifiers.length - 1]
          if ((e as KeyboardEvent).key.toLowerCase() !== keyMod) return
        }
        const fn = evalExpr(handlerExpr, ctx)
        if (typeof fn === 'function') fn(e)
      }
      continue
    }
    if (key.startsWith(':') || key.startsWith('v-bind:')) {
      const propName = key.startsWith(':') ? key.slice(1) : key.slice(6)
      props[propName] = evalExpr(node.attrs[key], ctx)
      continue
    }
    // Static attribute
    props[key] = node.attrs[key]
  }
  const children: VNode[] = []
  for (const child of node.children) {
    children.push(compileNode(child, ctx, hFn))
  }
  return hFn(node.tag, props, children.length ? children : null)
}

function interpolate(text: string, ctx: any, hFn: typeof h): VNode {
  // {{ expr }} → render the evaluated value
  const parts: string[] = []
  let lastIdx = 0
  const re = /\{\{([^}]+)\}\}/g
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index))
    parts.push(String(evalExpr(m[1].trim(), ctx)))
    lastIdx = m.index + m[0].length
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx))
  return hFn(Text, null, parts.join(''))
}

function evalExpr(expr: string, ctx: any): any {
  try {
    const keys = Object.keys(ctx)
    const fn = new Function(...keys, `"use strict"; return (${expr});`)
    return fn(...keys.map((k) => ctx[k]))
  } catch (err) {
    return undefined
  }
}

function assignExpr(expr: string, ctx: any, value: any): void {
  try {
    const keys = Object.keys(ctx)
    const fn = new Function(...keys, 'value', `"use strict"; ${expr} = value;`)
    fn(...keys.map((k) => ctx[k]), value)
  } catch {
    /* noop */
  }
}

// ----- Runtime directive registry --------------------------------------------

export interface DirectiveBinding {
  value: any
  oldValue: any
  arg?: string
  modifiers: Record<string, boolean>
  instance: any
}

export interface DirectiveDefinition {
  beforeMount?: (el: Element, binding: DirectiveBinding) => void
  mounted?: (el: Element, binding: DirectiveBinding) => void
  beforeUpdate?: (el: Element, binding: DirectiveBinding) => void
  updated?: (el: Element, binding: DirectiveBinding) => void
  beforeUnmount?: (el: Element, binding: DirectiveBinding) => void
  unmounted?: (el: Element, binding: DirectiveBinding) => void
}

const directives: Record<string, DirectiveDefinition> = {
  show: {
    beforeMount(el, { value }) {
      el.style.display = value ? '' : 'none'
    },
    updated(el, { value, oldValue }) {
      if (value !== oldValue) el.style.display = value ? '' : 'none'
    },
  },
  focus: {
    mounted(el) {
      ;(el as HTMLElement).focus()
    },
  },
  model: {
    mounted(el, { value }) {
      ;(el as HTMLInputElement).value = value ?? ''
    },
    updated(el, { value }) {
      if ((el as HTMLInputElement).value !== String(value)) {
        ;(el as HTMLInputElement).value = value ?? ''
      }
    },
  },
}

export function registerDirective(name: string, def: DirectiveDefinition): void {
  directives[name] = def
}

export function getDirective(name: string): DirectiveDefinition | undefined {
  return directives[name]
}
